import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MenuModule } from 'primeng/menu';
import { Menu } from 'primeng/menu';
import { MessageService } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

import { TableColumn } from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import {
  SearchFormComponent,
  FormFieldConfig,
} from '@shared/components/search-form/search-form.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import { DateFormatterService } from '@shared/services/date-formatter.service';
import { PermissionService } from '@app/core/services/permission.service';
import { P_PROCUREMENT } from '@app/core/constants/permissions';
import { VendorService } from '../services/vendor.service';
import { VendorSkuControllerService } from '@app/api/vendor-service/api/vendorSkuController.service';
import { VendorSkuDTO } from '@app/api/vendor-service/model/vendorSkuDTO';
import {
  VendorSkuDialogComponent,
  VendorSkuFormData,
} from './components/vendor-sku-dialog/vendor-sku-dialog.component';
import { ConfirmationDialogComponent } from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import { SkuOptionsService } from '@shared/services/sku-options.service';
import { withPaginatedSearchFields } from '@shared/utils/paginated-field.helpers';
import {
  VENDOR_MANAGE_SKU_SEARCH_CONFIG,
  VENDOR_MANAGE_SKU_DEFAULT_COLUMNS_ORDER,
  VENDOR_MANAGE_SKU_TABLE_COLUMNS,
  VENDOR_MANAGE_SKU_ACTION_COLUMN,
} from './vendor-manage-sku.config';
import {
  parseBlobHttpError,
  resolveVendorSkuErrorKey,
} from './vendor-sku-error.util';

interface VendorSKUItem {
  id: string;
  vendorId: string;
  vendorCode?: string;
  vendorName: string;
  skuCode: string;
  skuName?: string;
  vendorSkuCode: string;
  vendorProductName: string;
  description?: string;
  unitPrice: number;
  active: boolean;
  primaryFlag: boolean;
}

interface VendorSKUSearchForm {
  skuCode?: string;
  skuName?: string;
  vendorSkuCode?: string;
  vendorProductName?: string;
  status?: string;
}

@Component({
  selector: 'app-vendor-manage-sku',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    FormsModule,
    ButtonModule,
    ToastModule,
    TagModule,
    TooltipModule,
    MenuModule,
    AdvancedDataTableComponent,
    SearchFormComponent,
    AppButtonComponent,
    VendorSkuDialogComponent,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './vendor-manage-sku.component.html',
  styleUrl: './vendor-manage-sku.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VendorManageSkuComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private vendorService = inject(VendorService);
  private vendorSkuService = inject(VendorSkuControllerService);
  private readonly skuOptionsService = inject(SkuOptionsService);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);
  private dateFormatterService = inject(DateFormatterService);
  private permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private destroy$ = new Subject<void>();

  vendorId = signal<string | null>(null);
  vendorName = signal<string>('');
  vendorCode = signal<string>('');

  // Permission flags
  // Using specific submenu 'vendor' for procurement permissions
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('procurement.vendor.view');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('procurement.vendor.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('procurement.vendor.delete');
  });

  // Data signals
  vendorSkus = signal<VendorSKUItem[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Table configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      VENDOR_MANAGE_SKU_TABLE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of VENDOR_MANAGE_SKU_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Add actions column at the end if user has update or delete permission
    if (this.canUpdate() || this.canDelete()) {
      const actionsCol = fromConfig(VENDOR_MANAGE_SKU_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  // Search form configuration
  searchFormat = signal<FormFieldConfig[]>(
    withPaginatedSearchFields(VENDOR_MANAGE_SKU_SEARCH_CONFIG, {
      skuCode: {
        loadPage: (params) => this.skuOptionsService.getOptionsPage(params),
      },
    }),
  );

  // Search parameters
  searchParams = signal({
    skuCode: '',
    skuName: '',
    vendorSkuCode: '',
    vendorProductName: '',
    status: undefined as string | undefined,
    page: 1,
    size: 20,
  });

  // Menu
  @ViewChild('actionMenu') actionMenu?: Menu;
  @ViewChild(VendorSkuDialogComponent)
  vendorSkuDialog?: VendorSkuDialogComponent;
  currentMenuModel = signal<any[]>([]);
  selectedSkuData: VendorSKUItem | null = null;

  // Dialog state
  showSkuDialog = signal(false);
  editMode = signal(false);
  dialogLoading = signal(false);
  selectedSkuForEdit: VendorSKUItem | null = null;

  // Delete confirmation dialog
  showDeleteDialog = signal(false);
  skuToDelete: VendorSKUItem | null = null;
  deleteLoading = signal(false);

  // Confirmation dialog state for toggle status
  showToggleStatusDialog = signal(false);
  toggleStatusMessage = signal('');
  toggleStatusTitle = signal('');
  toggleStatusButtons = signal<any[]>([]);
  toggleStatusSku = signal<VendorSKUItem | null>(null);
  toggleStatusNewActive = signal<boolean>(false);

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const id = params.get('id');
      if (id) {
        this.vendorId.set(id);
        this.loadVendorInfo();
        this.loadVendorSkus();
      }
    });
  }

  private loadVendorInfo(): void {
    if (!this.vendorId()) return;

    this.vendorService.getVendorById(this.vendorId()!).subscribe({
      next: (vendor) => {
        if (vendor) {
          this.vendorCode.set(vendor.code || '');
          this.vendorName.set(vendor.name || '');
        }
      },
      error: (error) => {
        console.error('Error loading vendor info:', error);
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadVendorSkus(): void {
    if (!this.vendorId()) return;

    this.loading.set(true);
    const vendorId = Number.parseInt(this.vendorId() || '0');

    if (isNaN(vendorId)) {
      this.loading.set(false);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'vendor.messages.error.invalid_vendor_id',
        ),
      });
      return;
    }

    const params = this.searchParams();

    // Convert status filter to active boolean if provided
    let activeFilter: boolean | undefined = undefined;
    if (params.status === 'ACTIVE') {
      activeFilter = true;
    } else if (params.status === 'INACTIVE') {
      activeFilter = false;
    }

    this.vendorSkuService
      .page1(
        vendorId, // vendorId
        this.vendorCode() || undefined, // vendorCode
        params.vendorSkuCode || undefined, // vendorSkuCode
        params.vendorProductName || undefined, // vendorProductName
        params.skuCode || undefined, // skuCode
        params.skuName || undefined, // skuName
        undefined, // description
        activeFilter, // active
        params.page - 1, // page (0-based)
        params.size, // size
        undefined, // sortBy
        undefined, // ascending
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          let parsedResponse = response;

          // If response is a Blob, parse it to JSON
          if (response instanceof Blob) {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                parsedResponse = JSON.parse(reader.result as string);
                this.processVendorSkus(parsedResponse);
              } catch (error) {
                console.error('Error parsing Blob response:', error);
                this.loading.set(false);
                this.messageService.add({
                  severity: 'error',
                  summary: this.translateService.instant(
                    'common.messages.error',
                  ),
                  detail: 'Error parsing vendor SKUs response',
                });
              }
            };
            reader.readAsText(response);
          } else {
            this.processVendorSkus(parsedResponse);
          }
        },
        error: (error) => {
          console.error('Error loading vendor SKUs:', error);
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'vendor.messages.error.failed_to_load_vendor_skus',
            ),
          });
        },
      });
  }

  private processVendorSkus(response: any): void {
    if (response && response.pageContent) {
      // Map API response to component data
      const skus = response.pageContent.map((sku: any) => ({
        id: sku.id?.toString() || '',
        vendorId: sku.vendorId?.toString() || '',
        vendorCode: sku.vendorCode || this.vendorCode() || '',
        vendorName: sku.vendorName || this.vendorName() || '',
        skuCode: sku.skuCode || '',
        skuName: sku.skuName || '',
        vendorSkuCode: sku.vendorSkuCode || '',
        vendorProductName: sku.vendorProductName || '',
        description: sku.description || '',
        unitPrice: Number(sku.unitPrice) || 0,
        active: sku.active || false,
        primaryFlag: sku.primaryFlag === true,
      }));
      this.vendorSkus.set(skus);
      this.totalRecords.set(response.totalElements || 0);
    } else {
      console.warn('No pageContent in response');
    }
    this.loading.set(false);
  }

  onSearch(searchData: VendorSKUSearchForm): void {
    const updatedParams = {
      skuCode: searchData.skuCode || '',
      skuName: searchData.skuName || '',
      vendorSkuCode: searchData.vendorSkuCode || '',
      vendorProductName: searchData.vendorProductName || '',
      status: searchData.status || undefined,
      page: 1,
      size: this.searchParams().size,
    };
    this.searchParams.set(updatedParams);
    this.loadVendorSkus();
  }

  onReset(): void {
    this.searchParams.set({
      skuCode: '',
      skuName: '',
      vendorSkuCode: '',
      vendorProductName: '',
      status: undefined,
      page: 1,
      size: 20,
    });
    this.loadVendorSkus();
  }

  onPageChange(event: any): void {
    this.searchParams.set({
      ...this.searchParams(),
      page: event.page + 1,
      size: event.rows,
    });
    this.loadVendorSkus();
  }

  onBack(): void {
    this.router.navigate(['/procurement/vendor']);
  }

  // Dialog Methods
  onOpenAddSkuDialog(): void {
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.editMode.set(false);
    this.selectedSkuForEdit = null;
    this.showSkuDialog.set(true);
  }

  onOpenEditSkuDialog(sku: VendorSKUItem): void {
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permission Denied',
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    // Set selectedSkuForEdit first, then editMode, then show dialog
    this.selectedSkuForEdit = sku;
    this.editMode.set(true);
    // Use setTimeout to ensure selectedSkuForEdit is set before dialog opens
    setTimeout(() => {
      this.showSkuDialog.set(true);
    }, 0);
  }

  onDialogConfirm(formData: VendorSkuFormData): void {
    if (!this.vendorId()) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'vendor.messages.error.invalid_vendor_id',
        ),
      });
      return;
    }

    this.dialogLoading.set(true);
    const vendorId = Number.parseInt(this.vendorId() || '0');

    if (this.editMode() && this.selectedSkuForEdit) {
      // Update mode
      const skuData: VendorSkuDTO = {
        id: Number.parseInt(this.selectedSkuForEdit.id || '0'),
        vendorId: Number.parseInt(this.selectedSkuForEdit.vendorId || '0'),
        skuCode: this.selectedSkuForEdit.skuCode, // Include skuCode
        skuName: this.selectedSkuForEdit.skuName || '', // Include skuName
        vendorSkuCode: formData.vendorSkuCode,
        vendorProductName: formData.vendorProductName,
        description: formData.description || '',
        unitPrice: formData.unitPrice,
        active: formData.active,
        primaryFlag: formData.primaryFlag ?? false,
      };

      this.vendorSkuService
        .updateVendor1(
          Number.parseInt(this.selectedSkuForEdit.id || '0'),
          skuData,
        )
        .pipe(parseBlobHttpError(), takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.dialogLoading.set(false);
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'vendor.messages.success.sku_updated',
              ),
            });
            this.showSkuDialog.set(false);
            this.loadVendorSkus();
          },
          error: (error) => {
            this.dialogLoading.set(false);
            // Pass error to dialog to display inline
            if (this.vendorSkuDialog) {
              this.vendorSkuDialog.setError(error);
            }
          },
        });
    } else {
      // Add mode
      const skuCode = formData.skuCode || '';
      const skuName = formData.skuName || '';

      const skuData: VendorSkuDTO = {
        vendorId: vendorId,
        vendorSkuCode: formData.vendorSkuCode,
        skuName: skuName,
        skuCode: skuCode,
        vendorProductName: formData.vendorProductName,
        description: formData.description || '',
        unitPrice: formData.unitPrice,
        active: formData.active,
        primaryFlag: formData.primaryFlag ?? false,
      };

      this.vendorSkuService
        .addVendorSku(skuData)
        .pipe(parseBlobHttpError(), takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.dialogLoading.set(false);
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('common.messages.success'),
              detail: this.translateService.instant(
                'vendor.messages.success.sku_added',
              ),
            });
            this.showSkuDialog.set(false);
            this.loadVendorSkus();
          },
          error: (error) => {
            this.dialogLoading.set(false);
            // Pass error to dialog to display inline
            if (this.vendorSkuDialog) {
              this.vendorSkuDialog.setError(error);
            }
          },
        });
    }
  }

  onDialogCancel(): void {
    this.showSkuDialog.set(false);
    this.selectedSkuForEdit = null;
  }

  getDialogInitialData(): VendorSkuFormData | null {
    if (this.editMode() && this.selectedSkuForEdit) {
      return {
        skuCode: this.selectedSkuForEdit.skuCode,
        vendorSkuCode: this.selectedSkuForEdit.vendorSkuCode,
        vendorProductName: this.selectedSkuForEdit.vendorProductName,
        description: this.selectedSkuForEdit.description || '',
        unitPrice: this.selectedSkuForEdit.unitPrice,
        active: this.selectedSkuForEdit.active,
        primaryFlag: this.selectedSkuForEdit.primaryFlag,
      };
    }
    return null;
  }

  // Delete Methods
  onDeleteSku(sku: VendorSKUItem): void {
    if (!this.canDelete()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.skuToDelete = sku;
    this.showDeleteDialog.set(true);
  }

  onDeleteDialogonClick(action: string): void {
    if (action === 'confirm') {
      this.onDeleteConfirm();
    } else if (action === 'cancel') {
      this.onDeleteCancel();
    }
  }

  onDeleteConfirm(): void {
    if (!this.skuToDelete || !this.skuToDelete.id) return;

    this.deleteLoading.set(true);
    const skuId = Number.parseInt(this.skuToDelete.id);

    this.vendorSkuService
      .deleteById(skuId)
      .pipe(parseBlobHttpError(), takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.deleteLoading.set(false);
          this.showDeleteDialog.set(false);
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'vendor.messages.success.sku_deleted',
            ),
          });
          this.skuToDelete = null;
          this.loadVendorSkus();
        },
        error: (error) => {
          this.deleteLoading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateBackendError(
              error,
              'vendor.messages.error.delete_sku_failed',
            ),
          });
        },
      });
  }

  onDeleteCancel(): void {
    this.showDeleteDialog.set(false);
    this.skuToDelete = null;
  }

  // Toggle Active Status
  onToggleActiveStatus(sku: VendorSKUItem): void {
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant(
          'common.permissions.no_permission',
        ),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }

    const newActiveStatus = !sku.active;
    const action = newActiveStatus ? 'activate' : 'deactivate';
    const message = this.translateService.instant(
      `vendor.manage_sku.confirm_${action}`,
      { name: sku.vendorProductName || sku.vendorSkuCode },
    );
    const title = this.translateService.instant(
      newActiveStatus
        ? 'vendor.manage_sku.activate_title'
        : 'vendor.manage_sku.deactivate_title',
    );

    this.toggleStatusMessage.set(message);
    this.toggleStatusTitle.set(title);
    this.toggleStatusButtons.set([
      {
        label: this.translateService.instant('common.actions.cancel'),
        action: 'cancel',
        outlined: true,
      },
      {
        label: this.translateService.instant(
          newActiveStatus
            ? 'vendor.actions.activate'
            : 'vendor.actions.deactivate',
        ),
        action: 'confirm',
        severity: 'info',
      },
    ]);
    this.toggleStatusSku.set(sku);
    this.toggleStatusNewActive.set(newActiveStatus);
    this.showToggleStatusDialog.set(true);
  }

  /**
   * Handle toggle status confirmation dialog actions
   */
  onToggleStatusDialogAction(action: string): void {
    this.showToggleStatusDialog.set(false);

    if (action === 'confirm') {
      this.executeToggleActiveStatus(
        this.toggleStatusSku()!,
        this.toggleStatusNewActive(),
      );
    }

    // Reset state
    this.toggleStatusSku.set(null);
    this.toggleStatusNewActive.set(false);
  }

  /**
   * Execute toggle active status
   */
  private executeToggleActiveStatus(
    sku: VendorSKUItem,
    newActiveStatus: boolean,
  ): void {
    const skuData: VendorSkuDTO = {
      id: Number.parseInt(sku.id || '0'),
      vendorId: Number.parseInt(sku.vendorId || '0'),
      vendorSkuCode: sku.vendorSkuCode,
      vendorProductName: sku.vendorProductName,
      description: sku.description || '',
      unitPrice: sku.unitPrice,
      active: newActiveStatus,
      primaryFlag: sku.primaryFlag,
    };

    this.vendorSkuService
      .updateVendor1(Number.parseInt(sku.id || '0'), skuData)
      .pipe(parseBlobHttpError(), takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: newActiveStatus
              ? this.translateService.instant(
                  'vendor.messages.success.sku_activated',
                )
              : this.translateService.instant(
                  'vendor.messages.success.sku_deactivated',
                ),
          });
          this.loadVendorSkus();
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateBackendError(
              error,
              'vendor.messages.error.toggle_status_failed',
            ),
          });
        },
      });
  }

  private translateBackendError(error: any, fallbackKey: string): string {
    return this.translateService.instant(
      resolveVendorSkuErrorKey(error) ?? fallbackKey,
    );
  }
}
